import { useCallback, useMemo, useState } from 'react'

export interface FilterOption {
  value: string
  label: string
}

export interface FilterGroup {
  id: string
  label: string
  options: FilterOption[]
}

/** Selected option values per group id. Missing or empty = no constraint. */
export type FilterSelection = Record<string, string[]>

/** Values an item carries for one filter group. */
export type FilterAccessor<T> = (item: T, groupId: string) => string[]

/**
 * Filters items: OR within a group (any selected value matches), AND across
 * groups (every group with a selection must match).
 */
export function filterItems<T>(items: T[], selection: FilterSelection, getValues: FilterAccessor<T>): T[] {
  const active = Object.entries(selection).filter(([, values]) => values.length > 0)
  if (active.length === 0) return items
  return items.filter((item) =>
    active.every(([groupId, selected]) => {
      const values = getValues(item, groupId)
      return values.some((v) => selected.includes(v))
    })
  )
}

/** Unique options in first-seen order, from raw values. */
export function optionsFrom(values: string[]): FilterOption[] {
  const seen = new Set<string>()
  const out: FilterOption[] = []
  for (const value of values) {
    if (!value || seen.has(value)) continue
    seen.add(value)
    out.push({ value, label: value })
  }
  return out
}

/** Client-side filter state for a listing. */
export function useListingFilters<T>(items: T[], getValues: FilterAccessor<T>) {
  const [selection, setSelection] = useState<FilterSelection>({})

  const toggle = useCallback((groupId: string, value: string) => {
    setSelection((prev) => {
      const current = prev[groupId] ?? []
      const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
      return { ...prev, [groupId]: next }
    })
  }, [])

  const clear = useCallback(() => setSelection({}), [])

  const filtered = useMemo(() => filterItems(items, selection, getValues), [items, selection, getValues])
  const activeCount = Object.values(selection).reduce((n, values) => n + values.length, 0)

  return { selection, toggle, clear, filtered, activeCount }
}
